"use client";
import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

// services and utils imports
import { useWidgetClick } from "../../../../utils/dynamicHomeUtils/WidgetClickContext";
import { LAYOUT_TYPE, WIDGET_TYPE } from "../../../../constants/pageConstants/dynamicHomeConstants";
import { runOnNavigationIntent } from "../../../../utils/common/navigationIntent";
import { setIsNavigatingAction } from "../../../../store/slices/commonSlice";

/**
 * Renders the section header with title, subtext, and an optional CTA button.
 * @param {object} props - Component props
 * @param {object} props.sectionSettings - Section settings object containing header config
 * @param {boolean} props.isMobile - Whether the viewport is mobile
 * @param {string} [props.headingLevel="h2"] - HTML heading tag for the title (h1-h6)
 * @returns {React.ReactElement|null} The section header or null
 */
const SectionHeader = ({ sectionSettings, isMobile, headingLevel, widgetType, layoutSnapshot }) => {
  const isCollectionLayoutType =
    layoutSnapshot?.type === LAYOUT_TYPE.CAROUSEL || layoutSnapshot?.type === LAYOUT_TYPE.GRID;

  const isProductCollectionWithoutLayout = widgetType === WIDGET_TYPE.PRODUCT_COLLECTION && !isCollectionLayoutType;

  const WIDGETS_WITH_LARGE_HEADER_MARGIN = new Set([WIDGET_TYPE.PRODUCTS_WITH_TABS, WIDGET_TYPE.VIDEO]);

  const headerMarginTopDesktop =
    WIDGETS_WITH_LARGE_HEADER_MARGIN.has(widgetType) || isProductCollectionWithoutLayout ? "24px" : "4px";
  const dispatch = useDispatch();
  const { onCellClick } = useWidgetClick() ?? {};
  const cta = sectionSettings?.header?.cta;
  const showCTA = cta && sectionSettings?.header?.titleAlignment === "left";
  const ctaLink = cta?.link || cta?.url || "";

  /**
   * Tracks Widget Clicked (CTA) before navigating to the header CTA URL.
   * @param {React.MouseEvent} event - Click event from the CTA anchor.
   * @returns {void}
   */
  const onHeaderCtaClick = useCallback(
    (event) => {
      runOnNavigationIntent(event, () => dispatch(setIsNavigatingAction(true)));
      onCellClick?.({
        action: "CTA Clicked",
        item: {
          item_name: cta?.label || "View All",
          redirection_url: ctaLink || undefined,
        },
      });
    },
    [onCellClick, cta?.label, ctaLink],
  );

  const sectionPadding = {
    paddingLeft: `${sectionSettings?.padding?.left || 0}px`,
    paddingRight: `${sectionSettings?.padding?.right || 0}px`,
  };

  const titleStyle = {
    textAlign: sectionSettings?.header?.titleAlignment || "left",
    color: sectionSettings?.header?.titleColor || "#000000",
    fontSize: `${sectionSettings?.header?.mainTitleStyle?.fontSize}px` || (isMobile ? "22px" : "32px"),
    fontWeight: sectionSettings?.header?.mainTitleStyle?.fontWeight || 600,
    fontFamily: sectionSettings?.header?.mainTitleStyle?.fontFamily || "inherit",
  };

  const subtextStyle = {
    marginTop: isMobile ? "4px" : "8px",
    textAlign: sectionSettings?.header?.subtextAlignment || "left",
    color: sectionSettings?.header?.subtextColor || "#000000",
    fontSize: sectionSettings?.header?.subTextStyle?.fontSize
      ? `${sectionSettings.header.subTextStyle.fontSize}px`
      : "16px",
    fontWeight: sectionSettings?.header?.subTextStyle?.fontWeight || 400,
    fontFamily: sectionSettings?.header?.subTextStyle?.fontFamily || "inherit",
  };

  const ctaStyle = cta
    ? {
        color: cta.textColor || "#0a0a0a",
        borderColor: cta.borderColor || "#101010",
        borderRadius: `${cta.borderRadius || 12}px`,
        borderWidth: `${cta.borderThickness || 1}px`,
        width: cta.width || "auto",
      }
    : {};

  return (
    <div
      className={showCTA ? "flex items-start justify-between" : ""}
      style={{
        marginBottom: isMobile ? "12px" : headerMarginTopDesktop,
        ...sectionPadding,
      }}
    >
      <div className={showCTA ? "flex-1" : ""}>
        {sectionSettings?.header?.title
          ? React.createElement(headingLevel, { style: titleStyle, className: "m-0 p-0" }, sectionSettings.header.title)
          : null}
        {sectionSettings?.header?.subtext ? <p style={subtextStyle}>{sectionSettings.header.subtext}</p> : null}
      </div>

      {showCTA && ctaLink ? (
        <a
          href={ctaLink}
          aria-label={`${cta.label || "View All"} ${sectionSettings?.header?.title || ""}`.trim()}
          className="flex items-center gap-2 border border-solid px-16 py-8 font-medium transition-colors hover:opacity-80"
          style={ctaStyle}
          onClick={onHeaderCtaClick}
        >
          <span>{cta.label || "View All"}</span>
          {cta.showArrow && (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden={true}>
              <path
                d="M6 12l4-4-4-4"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </a>
      ) : (
        <></>
      )}
    </div>
  );
};

SectionHeader.propTypes = {
  sectionSettings: PropTypes.object,
  isMobile: PropTypes.bool.isRequired,
  headingLevel: PropTypes.oneOf(["h1", "h2", "h3", "h4", "h5", "h6"]),
  widgetType: PropTypes.string,
  layoutSnapshot: PropTypes.object,
};

export default SectionHeader;
